(function () {
  'use strict';

  /*
    SFG ENGINE / VEHICLE DTC DATABASE
    =================================

    Designed for use with:
      sfg-support-codes.js
      sfg-vehicle-decoder.js

    Lookup priority:
      1. Specific engine/platform rule
      2. Manufacturer rule
      3. Generic SAE rule
      4. Safe unknown-code handling

    Expected vehicle context:
    {
      manufacturer: 'gm' | 'ford' | 'ram' | 'other',
      year: 2020,
      make: 'Chevrolet',
      model: 'Silverado 2500HD',
      engine: 'L5P',
      displacementL: 6.6,
      fuel: 'Diesel',
      truckSize: '2500',
      body: 'Pickup'
    }

    Unknown manufacturer-specific codes MUST NOT be guessed.
  */


  /* ==========================================================
     HELPERS
     ========================================================== */

  function R(title, category, customerText, action, extra) {
    var out = {
      title: title,
      category: category,
      customerText: customerText,
      action: action
    };

    Object.keys(extra || {}).forEach(function (key) {
      out[key] = extra[key];
    });

    return out;
  }


  function merge(base, extra) {
    var out = {};

    Object.keys(base || {}).forEach(function (key) {
      out[key] = base[key];
    });

    Object.keys(extra || {}).forEach(function (key) {
      out[key] = extra[key];
    });

    return out;
  }


  function electrical(title, system, extra) {
    return R(
      title,
      'electrical_circuit',
      'This is primarily an electrical/circuit fault. Verify the relevant component is connected, then inspect the connector, terminals and wiring before assuming the problem is calibration related.',
      'inspect_connector_wiring',
      merge({
        system: system,
        circuitFault: true,
        stockTest: true,
        logRecommended: false,
        ticketRecommendedIfPersistent: true
      }, extra)
    );
  }


  function intermittent(title, system, extra) {
    return R(
      title,
      'intermittent_circuit',
      'This indicates an intermittent or unstable electrical signal. Inspect the connector, terminals and wiring for loose pins, damaged wiring, poor contact or movement-related faults.',
      'inspect_connector_wiring',
      merge({
        system: system,
        circuitFault: true,
        intermittent: true,
        stockTest: true,
        logRecommended: false,
        ticketRecommendedIfPersistent: true
      }, extra)
    );
  }


  function performance(title, system, text, extra) {
    return R(
      title,
      'range_performance',
      text,
      'check_system_performance',
      merge({
        system: system,
        performanceFault: true,
        stockTest: false,
        logRecommended: true,
        ticketRecommendedIfPersistent: true
      }, extra)
    );
  }


  function normalizeVehicle(vehicle) {
    vehicle = vehicle || {};

    var manufacturer = String(
      vehicle.manufacturer ||
      vehicle.make ||
      ''
    ).toLowerCase();

    if (
      manufacturer.indexOf('chevrolet') >= 0 ||
      manufacturer.indexOf('gmc') >= 0 ||
      manufacturer.indexOf('general motors') >= 0 ||
      manufacturer === 'gm'
    ) {
      manufacturer = 'gm';
    } else if (
      manufacturer.indexOf('ram') >= 0 ||
      manufacturer.indexOf('dodge') >= 0 ||
      manufacturer.indexOf('chrysler') >= 0 ||
      manufacturer.indexOf('stellantis') >= 0
    ) {
      manufacturer = 'ram';
    } else if (
      manufacturer.indexOf('ford') >= 0
    ) {
      manufacturer = 'ford';
    } else {
      manufacturer = manufacturer || 'other';
    }

    var year = parseInt(
      vehicle.year ||
      vehicle.modelYear ||
      vehicle.ModelYear ||
      0,
      10
    );

    var displacement = parseFloat(
      vehicle.displacementL ||
      vehicle.displacement ||
      vehicle.DisplacementL ||
      0
    );

    var engine = String(
      vehicle.engine ||
      vehicle.engineSummary ||
      vehicle.EngineModel ||
      ''
    ).trim();

    var fuel = String(
      vehicle.fuel ||
      vehicle.FuelTypePrimary ||
      ''
    ).trim();

    return {
      manufacturer: manufacturer,
      year: isFinite(year) ? year : 0,
      make: String(vehicle.make || vehicle.Make || ''),
      model: String(vehicle.model || vehicle.Model || ''),
      engine: engine,
      displacementL: isFinite(displacement) ? displacement : 0,
      fuel: fuel,
      truckSize: String(vehicle.truckSize || ''),
      body: String(vehicle.body || '')
    };
  }


  function contains(value, pattern) {
    return pattern.test(String(value || ''));
  }


  function near(value, target, tolerance) {
    return Math.abs(Number(value || 0) - target) <= tolerance;
  }


  function isDiesel(v) {
    return (
      /diesel/i.test(v.fuel) ||
      /duramax|cummins|power\s*stroke/i.test(v.engine)
    );
  }


  function isGm(v) {
    return v.manufacturer === 'gm';
  }


  function isFord(v) {
    return v.manufacturer === 'ford';
  }


  function isRam(v) {
    return v.manufacturer === 'ram';
  }


  function isGm66Diesel(v) {
    return (
      isGm(v) &&
      isDiesel(v) &&
      (
        near(v.displacementL, 6.6, 0.2) ||
        /LB7|LLY|LBZ|LMM|LML|LGH|L5P|L5D/i.test(v.engine)
      )
    );
  }


  function isModernGm66(v) {
    return (
      isGm66Diesel(v) &&
      (
        /L5P|L5D/i.test(v.engine) ||
        v.year >= 2017
      )
    );
  }


  function isPreLmlDuramax(v) {
    return (
      isGm66Diesel(v) &&
      (
        /LB7|LLY|LBZ|LMM/i.test(v.engine) ||
        (v.year >= 2001 && v.year <= 2010)
      )
    );
  }


  function isLmlFamily(v) {
    return (
      isGm66Diesel(v) &&
      (
        /LML|LGH/i.test(v.engine) ||
        (v.year >= 2011 && v.year <= 2016)
      )
    );
  }


  function isLwn(v) {
    return (
      isGm(v) &&
      (
        /\bLWN\b/i.test(v.engine) ||
        (
          isDiesel(v) &&
          near(v.displacementL, 2.8, 0.2) &&
          /Colorado|Canyon/i.test(v.model)
        )
      )
    );
  }


  function isFord67(v) {
    return (
      isFord(v) &&
      isDiesel(v) &&
      (
        near(v.displacementL, 6.7, 0.2) ||
        /6\.7.*Power\s*Stroke|Power\s*Stroke.*6\.7/i.test(v.engine)
      )
    );
  }


  function isRam67(v) {
    return (
      isRam(v) &&
      isDiesel(v) &&
      (
        near(v.displacementL, 6.7, 0.2) ||
        /6\.7.*Cummins|Cummins.*6\.7/i.test(v.engine)
      )
    );
  }


  function isRam59(v) {
    return (
      isRam(v) &&
      isDiesel(v) &&
      (
        near(v.displacementL, 5.9, 0.2) ||
        /5\.9.*Cummins|Cummins.*5\.9/i.test(v.engine)
      )
    );
  }


  /* ==========================================================
     GENERIC SAE EXACT RULES
     ========================================================== */

  var G = {};


  /* ---------- MAF ---------- */

  G.P0100 = electrical(
    'Mass Air Flow Sensor Circuit Fault',
    'MAF'
  );

  G.P0101 = performance(
    'Mass Air Flow Sensor Range / Performance',
    'MAF',
    'The MAF signal is present but does not agree with expected engine airflow. Check the intake tract, MAF installation and charge-air system for leaks or restrictions. If the intake or MAF housing diameter changed from the configuration originally tuned for, updated tuning may be required.',
    {
      airflowFault: true,
      checkLeaks: true,
      checkIntakeConfiguration: true,
      hardwareRevisionPossible: true
    }
  );

  G.P0102 = electrical(
    'Mass Air Flow Sensor Circuit Low',
    'MAF'
  );

  G.P0103 = electrical(
    'Mass Air Flow Sensor Circuit High',
    'MAF'
  );

  G.P0104 = intermittent(
    'Mass Air Flow Sensor Circuit Intermittent',
    'MAF'
  );


  /* ---------- MAP / BARO ---------- */

  G.P0105 = electrical(
    'MAP / Barometric Pressure Sensor Circuit Fault',
    'MAP'
  );

  G.P0106 = performance(
    'MAP / Barometric Pressure Range / Performance',
    'MAP',
    'The pressure signal is present but does not agree with expected operating conditions. Check the MAP/boost sensor installation and inspect the intake and charge-air system for leaks or restrictions. A datalog is normally more useful than immediately replacing the sensor.',
    {
      airflowFault: true,
      boostFault: true,
      checkLeaks: true
    }
  );

  G.P0107 = electrical(
    'MAP / Barometric Pressure Sensor Circuit Low',
    'MAP'
  );

  G.P0108 = electrical(
    'MAP / Barometric Pressure Sensor Circuit High',
    'MAP'
  );

  G.P0109 = intermittent(
    'MAP / Barometric Pressure Sensor Circuit Intermittent',
    'MAP'
  );


  /* ---------- IAT ---------- */

  G.P0110 = electrical(
    'Intake Air Temperature Sensor Circuit Fault',
    'IAT'
  );

  G.P0112 = electrical(
    'Intake Air Temperature Sensor Circuit Low',
    'IAT'
  );

  G.P0113 = electrical(
    'Intake Air Temperature Sensor Circuit High',
    'IAT'
  );

  G.P0114 = intermittent(
    'Intake Air Temperature Sensor Circuit Intermittent',
    'IAT'
  );


  /* ---------- BOOST ---------- */

  G.P0234 = performance(
    'Turbocharger / Supercharger Overboost',
    'boost',
    'Actual boost is exceeding the controller target. Check the intake/charge system and turbo control hardware. On a monitored VGT system, SFG will want to compare commanded vane position, actual vane feedback and boost response.',
    {
      boostFault: true,
      vgtRelevant: true,
      compareCommandedActual: true
    }
  );

  G.P0299 = performance(
    'Turbocharger / Supercharger Underboost',
    'boost',
    'Actual boost is below the expected target. Check the intake and charge-air system for leaks first. On a monitored VGT system, commanded versus actual vane position can help distinguish a turbo/actuator problem from an airflow or charge-leak problem.',
    {
      boostFault: true,
      airflowFault: true,
      vgtRelevant: true,
      checkLeaks: true,
      compareCommandedActual: true
    }
  );

  G.P2262 = performance(
    'Turbo Boost Pressure Not Detected / Mechanical',
    'boost',
    'The controller is not seeing the expected boost response. Inspect the intake and charge-air system and turbo hardware. This is not automatically a tuning fault.',
    {
      boostFault: true,
      mechanicalPossible: true,
      checkLeaks: true
    }
  );

  G.P2263 = performance(
    'Turbocharger / Supercharger Boost System Performance',
    'boost',
    'The boost system is not performing as expected. Check for intake/charge leaks and inspect turbo control operation. SFG will normally want operating data before determining whether the issue is calibration or hardware related.',
    {
      boostFault: true,
      vgtRelevant: true,
      checkLeaks: true
    }
  );


  /* ---------- VGT CONTROL ---------- */

  G.P003A = performance(
    'Turbocharger Boost Control Position Not Learned / Learning Limit',
    'vgt_position',
    'The turbocharger vane/actuator position has not learned correctly or cannot reach the expected learned position. Inspect actuator and turbo vane movement. SFG will normally want commanded and actual vane-position data where feedback is available.',
    {
      boostFault: true,
      vgtRelevant: true,
      vgtFeedbackFault: true,
      compareCommandedActual: true,
      mechanicalPossible: true
    }
  );

  G.P00AF = performance(
    'Turbocharger Boost Control Module Performance',
    'turbo_control',
    'The turbo control system is not responding as expected. Inspect turbo actuator wiring and operation, then compare commanded and actual turbo position where feedback is available.',
    {
      boostFault: true,
      vgtRelevant: true,
      compareCommandedActual: true
    }
  );

  G.P0045 = electrical(
    'Turbocharger Boost Control A Circuit / Open',
    'turbo_control',
    {
      vgtRelevant: true
    }
  );

  G.P0046 = performance(
    'Turbocharger Boost Control A Range / Performance',
    'turbo_control',
    'The turbo control system is not responding within the expected range. Verify wiring and connectors, then compare commanded turbo/vane position with actual feedback if the vehicle provides it.',
    {
      boostFault: true,
      vgtRelevant: true,
      compareCommandedActual: true
    }
  );

  G.P0047 = electrical(
    'Turbocharger Boost Control A Circuit Low',
    'turbo_control',
    {
      vgtRelevant: true
    }
  );

  G.P0048 = electrical(
    'Turbocharger Boost Control A Circuit High',
    'turbo_control',
    {
      vgtRelevant: true
    }
  );

  G.P2562 = electrical(
    'Turbocharger Boost Control Position Sensor A Circuit',
    'vgt_position',
    {
      vgtFeedbackFault: true
    }
  );

  G.P2563 = performance(
    'Turbocharger Boost Control Position Sensor A Range / Performance',
    'vgt_position',
    'Turbo position feedback is present but is not behaving as expected. Inspect the actuator/turbo mechanism and wiring. SFG will normally compare commanded and actual vane position.',
    {
      boostFault: true,
      vgtRelevant: true,
      vgtFeedbackFault: true,
      compareCommandedActual: true
    }
  );

  G.P2564 = electrical(
    'Turbocharger Boost Control Position Sensor A Circuit Low',
    'vgt_position',
    {
      vgtFeedbackFault: true
    }
  );

  G.P2565 = electrical(
    'Turbocharger Boost Control Position Sensor A Circuit High',
    'vgt_position',
    {
      vgtFeedbackFault: true
    }
  );

  G.P2566 = intermittent(
    'Turbocharger Boost Control Position Sensor A Circuit Intermittent',
    'vgt_position',
    {
      vgtFeedbackFault: true
    }
  );

  G.P2598 = performance(
    'Turbocharger Boost Control Position Performance — Stuck Low',
    'vgt_position',
    'The turbo/vane position is not following the expected command and appears stuck toward one end of its travel. Inspect actuator and turbo operation and compare commanded versus actual position.',
    {
      boostFault: true,
      vgtRelevant: true,
      mechanicalPossible: true,
      compareCommandedActual: true
    }
  );

  G.P2599 = performance(
    'Turbocharger Boost Control Position Performance — Stuck High',
    'vgt_position',
    'The turbo/vane position is not following the expected command and appears stuck toward one end of its travel. Inspect actuator and turbo operation and compare commanded versus actual position.',
    {
      boostFault: true,
      vgtRelevant: true,
      mechanicalPossible: true,
      compareCommandedActual: true
    }
  );


  /* ---------- HIGH-PRESSURE FUEL ---------- */

  G.P0087 = performance(
    'Fuel Rail / System Pressure Too Low',
    'fuel_pressure',
    'Actual fuel pressure is below the required or commanded pressure. This is a fuel-system performance fault, not automatically a rail-pressure-sensor fault. SFG will normally want commanded versus actual rail pressure and other relevant fuel-system data.',
    {
      fuelFault: true,
      railPressureFault: true,
      compareCommandedActual: true
    }
  );

  G.P0088 = performance(
    'Fuel Rail / System Pressure Too High',
    'fuel_pressure',
    'Actual fuel pressure is above the required or commanded pressure. SFG will normally want commanded versus actual rail pressure and regulator/control information.',
    {
      fuelFault: true,
      railPressureFault: true,
      compareCommandedActual: true
    }
  );

  G.P0089 = performance(
    'Fuel Pressure Regulator Performance',
    'fuel_pressure',
    'The fuel-pressure control system is not regulating pressure as expected. Verify that the fuel-system hardware still matches the tuned configuration and collect commanded versus actual rail-pressure data.',
    {
      fuelFault: true,
      railPressureFault: true,
      compareCommandedActual: true,
      checkHardwareChanges: true
    }
  );

  G.P0090 = electrical(
    'Fuel Pressure Regulator 1 Control Circuit / Open',
    'fuel_pressure_regulator',
    {
      fuelFault: true
    }
  );

  G.P0091 = electrical(
    'Fuel Pressure Regulator 1 Control Circuit Low',
    'fuel_pressure_regulator',
    {
      fuelFault: true
    }
  );

  G.P0092 = electrical(
    'Fuel Pressure Regulator 1 Control Circuit High',
    'fuel_pressure_regulator',
    {
      fuelFault: true
    }
  );

  G.P0093 = performance(
    'Fuel System Large Leak Detected',
    'fuel_system',
    'The controller has detected fuel-system behavior consistent with a significant pressure loss. Vehicle-side fuel-system diagnosis should be performed before assuming a calibration problem.',
    {
      fuelFault: true,
      likelyVehicleSide: true
    }
  );

  G.P008A = performance(
    'Low-Pressure Fuel System Pressure Too Low',
    'fuel_supply',
    'The supply side of the fuel system is below expected pressure. Check the low-pressure fuel supply system before assuming a tuning issue.',
    {
      fuelFault: true,
      likelyVehicleSide: true
    }
  );

  G.P008B = performance(
    'Low-Pressure Fuel System Pressure Too High',
    'fuel_supply',
    'The supply side of the fuel system is above expected pressure. Check the low-pressure fuel supply/control system.',
    {
      fuelFault: true,
      likelyVehicleSide: true
    }
  );

  G.P228C = performance(
    'Fuel Pressure Regulator Control Performance — Low Pressure',
    'fuel_pressure',
    'Fuel-pressure control has reached or exceeded its control limits while pressure remains too low. SFG will normally want commanded versus actual rail pressure and relevant fuel-system data.',
    {
      fuelFault: true,
      railPressureFault: true,
      compareCommandedActual: true
    }
  );

  G.P228D = performance(
    'Fuel Pressure Regulator Control Performance — High Pressure',
    'fuel_pressure',
    'Fuel-pressure control has reached or exceeded its control limits while pressure remains too high. SFG will normally want commanded versus actual rail pressure and relevant regulator data.',
    {
      fuelFault: true,
      railPressureFault: true,
      compareCommandedActual: true
    }
  );


  /* ---------- RAIL PRESSURE SENSOR ---------- */

  G.P0190 = electrical(
    'Fuel Rail Pressure Sensor Circuit Fault',
    'rail_pressure_sensor',
    {
      fuelFault: true,
      railPressureSensorFault: true
    }
  );

  G.P0191 = performance(
    'Fuel Rail Pressure Sensor Range / Performance',
    'rail_pressure_sensor',
    'The rail-pressure signal is present but is not behaving plausibly. Check the sensor, connector and wiring and compare commanded versus actual rail pressure before condemning high-pressure fuel hardware.',
    {
      fuelFault: true,
      railPressureSensorFault: true,
      compareCommandedActual: true
    }
  );

  G.P0192 = electrical(
    'Fuel Rail Pressure Sensor Circuit Low',
    'rail_pressure_sensor',
    {
      fuelFault: true,
      railPressureSensorFault: true
    }
  );

  G.P0193 = electrical(
    'Fuel Rail Pressure Sensor Circuit High',
    'rail_pressure_sensor',
    {
      fuelFault: true,
      railPressureSensorFault: true
    }
  );

  G.P0194 = intermittent(
    'Fuel Rail Pressure Sensor Circuit Intermittent',
    'rail_pressure_sensor',
    {
      fuelFault: true,
      railPressureSensorFault: true
    }
  );


  /* ---------- FUEL SUPPLY ---------- */

  G.P2635 = performance(
    'Fuel Pump Flow Performance',
    'fuel_supply',
    'The fuel-delivery system is not achieving expected flow or performance. This can indicate a vehicle-side supply or control problem and should not automatically be treated as a calibration issue.',
    {
      fuelFault: true,
      likelyVehicleSide: true
    }
  );


  /* ---------- FUEL LEVEL ---------- */

  G.P0460 = electrical(
    'Fuel Level Sensor Circuit Fault',
    'fuel_level'
  );

  G.P0461 = performance(
    'Fuel Level Sensor Range / Performance',
    'fuel_level',
    'The reported fuel level is not changing or behaving as expected. This normally points toward the fuel-level sender, wiring or related vehicle-side system rather than engine tuning.',
    {
      likelyVehicleSide: true,
      logRecommended: false
    }
  );

  G.P0462 = electrical(
    'Fuel Level Sensor Circuit Low',
    'fuel_level'
  );

  G.P0463 = electrical(
    'Fuel Level Sensor Circuit High',
    'fuel_level'
  );

  G.P0464 = intermittent(
    'Fuel Level Sensor Circuit Intermittent',
    'fuel_level'
  );


  /* ---------- CRANK SENSOR ---------- */

  G.P0335 = electrical(
    'Crankshaft Position Sensor A Circuit',
    'crank_position',
    {
      startingFault: true,
      noStartRelevant: true
    }
  );

  G.P0336 = performance(
    'Crankshaft Position Sensor A Range / Performance',
    'crank_position',
    'The crankshaft-position signal is present but does not match the expected pattern. Inspect the sensor, wiring, tone/reluctor wheel and related mechanical conditions.',
    {
      startingFault: true,
      noStartRelevant: true,
      stockTest: true,
      logRecommended: false
    }
  );

  G.P0337 = electrical(
    'Crankshaft Position Sensor A Circuit Low',
    'crank_position',
    {
      startingFault: true,
      noStartRelevant: true
    }
  );

  G.P0338 = electrical(
    'Crankshaft Position Sensor A Circuit High',
    'crank_position',
    {
      startingFault: true,
      noStartRelevant: true
    }
  );

  G.P0339 = intermittent(
    'Crankshaft Position Sensor A Circuit Intermittent',
    'crank_position',
    {
      startingFault: true,
      noStartRelevant: true
    }
  );


  /* ---------- CAM SENSOR ---------- */

  G.P0340 = electrical(
    'Camshaft Position Sensor A Circuit',
    'cam_position',
    {
      startingFault: true,
      noStartRelevant: true
    }
  );

  G.P0341 = performance(
    'Camshaft Position Sensor A Range / Performance',
    'cam_position',
    'The camshaft-position signal does not match the expected pattern. Inspect the sensor, wiring and mechanical timing/trigger system before assuming a tune problem.',
    {
      startingFault: true,
      noStartRelevant: true,
      stockTest: true,
      logRecommended: false
    }
  );

  G.P0342 = electrical(
    'Camshaft Position Sensor A Circuit Low',
    'cam_position'
  );

  G.P0343 = electrical(
    'Camshaft Position Sensor A Circuit High',
    'cam_position'
  );

  G.P0344 = intermittent(
    'Camshaft Position Sensor A Circuit Intermittent',
    'cam_position'
  );


  /* ---------- SENSOR REFERENCE VOLTAGE ---------- */

  G.P0641 = electrical(
    'Sensor Reference Voltage A Circuit / Open',
    'sensor_reference',
    {
      referenceVoltageFault: true,
      multipleSensorsPossible: true
    }
  );

  G.P0651 = electrical(
    'Sensor Reference Voltage B Circuit / Open',
    'sensor_reference',
    {
      referenceVoltageFault: true,
      multipleSensorsPossible: true
    }
  );

  G.P0697 = electrical(
    'Sensor Reference Voltage C Circuit / Open',
    'sensor_reference',
    {
      referenceVoltageFault: true,
      multipleSensorsPossible: true
    }
  );


  /* ---------- PEDAL ---------- */

  G.P2138 = R(
    'Accelerator Pedal Position Sensor Correlation',
    'pedal_correlation',
    'The accelerator-pedal position signals do not agree with one another. This generally requires pedal, connector, wiring or electrical diagnosis rather than a tune adjustment.',
    'inspect_connector_wiring',
    {
      circuitFault: true,
      likelyVehicleSide: true,
      stockTest: true,
      ticketRecommendedIfPersistent: true
    }
  );


  /* ---------- EGR ---------- */

  G.P0401 = performance(
    'EGR Flow Insufficient',
    'egr',
    'The controller is not seeing the expected EGR airflow response. First verify that the EGR and throttle hardware and connector state match the vehicle configuration the tune was written for.',
    {
      airflowFault: true,
      checkTuneConfiguration: true
    }
  );

  G.P0402 = performance(
    'EGR Flow Excessive',
    'egr',
    'The controller is seeing more EGR flow than expected. Verify that the EGR and throttle hardware and connector state match the tuned configuration.',
    {
      airflowFault: true,
      checkTuneConfiguration: true
    }
  );


  /* ---------- ECM / PROGRAMMING ---------- */

  G.P0601 = R(
    'ECM Internal Memory Checksum Error',
    'controller_internal',
    'The ECM has detected an internal memory/checksum problem. Create an SFG support ticket and include any recent programming or controller-replacement history.',
    'ticket',
    {
      controllerFault: true,
      programmingRelevant: true,
      ticketRecommended: true
    }
  );

  G.P0602 = R(
    'Control Module Programming Error',
    'controller_programming',
    'The control module is reporting a programming or configuration problem. Create an SFG support ticket and include any recent ECM/TCM replacement or programming history.',
    'ticket',
    {
      controllerFault: true,
      programmingRelevant: true,
      ticketRecommended: true
    }
  );

  G.P0606 = R(
    'ECM / PCM Processor Fault',
    'controller_internal',
    'The controller is reporting an internal processor fault. Record the exact code and create an SFG ticket before assuming a calibration revision will correct it.',
    'ticket',
    {
      controllerFault: true,
      ticketRecommended: true
    }
  );


  /* ---------- SECURITY ---------- */

  G.P0513 = R(
    'Incorrect Immobilizer Key',
    'security',
    'The vehicle is reporting an immobilizer/security-key problem. If the ECM, BCM, keys, ignition or security-related module has recently been replaced or programmed, include that information with the ticket.',
    'check_security_system',
    {
      securityFault: true,
      noStartRelevant: true,
      likelyVehicleSide: true,
      ticketRecommendedIfPersistent: true
    }
  );

  G.P0633 = R(
    'Immobilizer Key Not Programmed',
    'security',
    'The controller is reporting that immobilizer/security information has not been learned or programmed correctly. Include any controller, BCM or key replacement/programming history with the ticket.',
    'check_security_system',
    {
      securityFault: true,
      noStartRelevant: true,
      controllerReplacementRelevant: true,
      ticketRecommended: true
    }
  );


  /* ---------- TRANSMISSION ---------- */

  G.P0700 = R(
    'Transmission Control System Requested MIL',
    'transmission',
    'P0700 indicates that the transmission controller has stored its own fault information. Scan the TCM directly and include the actual transmission codes with the support request.',
    'scan_tcm',
    {
      transmissionFault: true,
      needsAdditionalCodes: true,
      ticketRecommendedIfPersistent: true
    }
  );


  /* ---------- AFTERTREATMENT ---------- */

  G.P20EE = performance(
    'SCR NOx Catalyst Efficiency Below Threshold',
    'aftertreatment',
    'The SCR/aftertreatment system is not achieving the expected emissions-conversion performance. Verify that the installed exhaust and emissions hardware matches the configuration the tune was written for. Exhaust-system changes may require updated tuning.',
    {
      aftertreatmentFault: true,
      exhaustRelevant: true,
      checkHardwareChanges: true
    }
  );

  G.P207F = performance(
    'Reductant Quality Performance',
    'aftertreatment',
    'The reductant/SCR system is not producing the expected result. Verify the current emissions/exhaust configuration matches what the tune was written for before assuming a calibration fault.',
    {
      aftertreatmentFault: true,
      exhaustRelevant: true,
      checkHardwareChanges: true
    }
  );

  G.P204F = performance(
    'Reductant System Performance',
    'aftertreatment',
    'The reductant system is not operating within expected limits. Confirm that the vehicle hardware and emissions configuration match the tuned setup.',
    {
      aftertreatmentFault: true,
      exhaustRelevant: true,
      checkHardwareChanges: true
    }
  );

  G.P2002 = performance(
    'Diesel Particulate Filter Efficiency Below Threshold',
    'aftertreatment',
    'The controller is not seeing the expected DPF performance. Exhaust-system condition or configuration can be relevant. Describe any exhaust changes made since the tune was ordered.',
    {
      aftertreatmentFault: true,
      exhaustRelevant: true,
      checkHardwareChanges: true
    }
  );

  G.P2463 = performance(
    'Diesel Particulate Filter Soot Accumulation',
    'aftertreatment',
    'The controller is reporting excessive calculated or measured DPF soot loading. Verify the installed exhaust/aftertreatment hardware matches the tuned configuration.',
    {
      aftertreatmentFault: true,
      exhaustRelevant: true,
      likelyVehicleSide: true
    }
  );


  /* ==========================================================
     VEHICLE-SPECIFIC RULES
     ========================================================== */

  var VEHICLE_RULES = [];


  /*
    GM 2017+ 6.6L diesel / L5P / L5D
  */

  VEHICLE_RULES.push({
    id: 'gm-modern-66-diesel',

    match: function (v) {
      return isModernGm66(v);
    },

    exact: {
      P1089: performance(
        'Fuel Rail Pressure Performance During Deceleration',
        'fuel_pressure',
        'On this GM 6.6L diesel application, P1089 is a fuel-rail-pressure performance fault associated with pressure behavior during deceleration. SFG will want commanded versus actual rail pressure and relevant fuel-control data. Treat it as a fuel-system performance issue rather than a turbo fault.',
        {
          fuelFault: true,
          railPressureFault: true,
          compareCommandedActual: true,
          vehicleSpecific: true
        }
      )
    }
  });


  /*
    Earlier Duramax — LB7 / LLY / LBZ / LMM
  */

  VEHICLE_RULES.push({
    id: 'gm-pre-lml-duramax',

    match: function (v) {
      return isPreLmlDuramax(v);
    },

    exact: {
      P1093: performance(
        'Fuel Rail Pressure Low During High Demand',
        'fuel_pressure',
        'On this Duramax application, P1093 indicates that actual rail pressure is falling below the required pressure under higher fuel demand. Check commanded versus actual rail pressure and investigate the fuel supply/high-pressure system before assuming a calibration fault.',
        {
          fuelFault: true,
          railPressureFault: true,
          compareCommandedActual: true,
          vehicleSpecific: true
        }
      )
    }
  });


  /*
    Duramax injector-group circuit faults.
    These are deliberately vehicle-specific because the group meaning
    should not be applied blindly to unrelated makes.
  */

  VEHICLE_RULES.push({
    id: 'gm-duramax-injector-circuits',

    match: function (v) {
      return isGm66Diesel(v);
    },

    exact: {
      P2146: electrical(
        'Injector Positive Voltage Control Circuit — Group 1',
        'injector_voltage',
        {
          fuelFault: true,
          injectorCircuitFault: true,
          vehicleSpecific: true
        }
      ),

      P2149: electrical(
        'Injector Positive Voltage Control Circuit — Group 2',
        'injector_voltage',
        {
          fuelFault: true,
          injectorCircuitFault: true,
          vehicleSpecific: true
        }
      ),

      P2152: electrical(
        'Injector Positive Voltage Control Circuit — Group 3',
        'injector_voltage',
        {
          fuelFault: true,
          injectorCircuitFault: true,
          vehicleSpecific: true
        }
      ),

      P2155: electrical(
        'Injector Positive Voltage Control Circuit — Group 4',
        'injector_voltage',
        {
          fuelFault: true,
          injectorCircuitFault: true,
          vehicleSpecific: true
        }
      )
    }
  });


  /*
    LWN exception.

    SFG does not want the automated support tool instructing a
    Colorado/Canyon 2.8L LWN customer to flash stock as a generic
    circuit-fault diagnostic step.
  */

  function applyVehicleExceptions(result, vehicle) {
    if (!result) return result;

    var out = merge(result, {});

    if (isLwn(vehicle) && out.stockTest) {
      out.stockTest = false;
      out.stockTestBlocked = true;
      out.stockTestNote =
        'Do not use the generic stock-flash comparison procedure on this LWN application. Inspect the relevant connector/wiring and create an SFG ticket if the fault remains.';
    }

    return out;
  }


  /* ==========================================================
     SAFE FAMILY FALLBACKS
     ========================================================== */

  var F = [
    {
      pattern: /^U0[0-9A-F]{3}$/,
      title: 'Vehicle Network / Communication Fault',
      category: 'vehicle_network',
      customerText:
        'This is a vehicle network/communication fault. Record the exact code and which module reported it. Check vehicle power and network connections and remove unnecessary aftermarket OBD/CAN devices. Some vehicles can also have communication interference from OEM modules.',
      action: 'check_vehicle_network',
      networkFault: true,
      stockTest: false,
      logRecommended: false,
      ticketRecommendedIfPersistent: true
    },

    {
      pattern: /^U1[0-9A-F]{3}$/,
      title: 'Manufacturer-Specific Network Fault',
      category: 'vehicle_network',
      customerText:
        'This is a manufacturer-specific network/communication code. SFG will retain the exact code rather than guessing its vehicle-specific definition. Check vehicle/network power and connections and include the code with the ticket.',
      action: 'collect_and_continue',
      networkFault: true,
      manufacturerSpecific: true,
      ticketRecommendedIfPersistent: true
    },

    {
      pattern: /^P1[0-9A-F]{3}$/,
      title: 'Manufacturer-Specific Powertrain Code',
      category: 'manufacturer_specific',
      customerText:
        'This is a manufacturer-specific powertrain code. Its definition depends on the vehicle. If SFG has a rule for the decoded vehicle it will be used; otherwise the exact code will be retained for staff review rather than guessed.',
      action: 'collect_and_continue',
      manufacturerSpecific: true,
      ticketRecommended: true
    },

    {
      pattern: /^P3[0-9A-F]{3}$/,
      title: 'Vehicle-Specific Powertrain Code',
      category: 'manufacturer_specific',
      customerText:
        'This code may have a vehicle-specific definition. The exact code will be retained for SFG review rather than assigning an automated diagnosis that may be incorrect.',
      action: 'collect_and_continue',
      manufacturerSpecific: true,
      ticketRecommended: true
    },

    {
      pattern: /^P0[0-9A-F]{3}$/,
      title: 'Generic Powertrain Fault',
      category: 'generic_powertrain',
      customerText:
        'This is a valid generic powertrain code, but SFG does not currently have a specific automated diagnostic rule for it. The exact code will be included with the support information.',
      action: 'collect_and_continue',
      ticketRecommendedIfPersistent: true
    }
  ];


  /* ==========================================================
     LOOKUP
     ========================================================== */

  function familyMatch(code, family) {
    if (!family || !family.pattern) return false;

    family.pattern.lastIndex = 0;

    return family.pattern.test(code);
  }


  function resolve(code, vehicle) {
    code = String(code || '').toUpperCase().trim();

    var v = normalizeVehicle(vehicle);

    /*
      Most-specific vehicle rules first.
    */
    for (var i = 0; i < VEHICLE_RULES.length; i++) {
      var rule = VEHICLE_RULES[i];

      if (!rule.match(v)) continue;

      if (rule.exact && rule.exact[code]) {
        return applyVehicleExceptions(
          merge(
            {
              code: code,
              matched: true,
              matchType: 'vehicle_exact',
              vehicleRule: rule.id
            },
            rule.exact[code]
          ),
          v
        );
      }
    }

    /*
      Generic exact SAE rule.
    */
    if (G[code]) {
      return applyVehicleExceptions(
        merge(
          {
            code: code,
            matched: true,
            matchType: 'exact'
          },
          G[code]
        ),
        v
      );
    }

    /*
      Safe family fallback.
    */
    for (var j = 0; j < F.length; j++) {
      if (familyMatch(code, F[j])) {
        return applyVehicleExceptions(
          merge(
            {
              code: code,
              matched: true,
              matchType: 'family'
            },
            F[j]
          ),
          v
        );
      }
    }

    return {
      code: code,
      matched: false,
      matchType: 'none',
      title: 'Code not specifically recognized',
      category: 'unknown',
      customerText:
        'SFG does not currently have a specific automated diagnostic rule for this code. The exact code will be retained with the support request rather than guessed.',
      action: 'collect_and_continue',
      ticketRecommended: true
    };
  }


  /* ==========================================================
     MULTI-CODE CORRELATION
     ========================================================== */

  function addInsight(list, key, title, text, extra) {
    for (var i = 0; i < list.length; i++) {
      if (list[i].key === key) return;
    }

    list.push(
      merge(
        {
          key: key,
          title: title,
          text: text
        },
        extra || {}
      )
    );
  }


  function analyzeSet(codes, vehicle) {
    var v = normalizeVehicle(vehicle);

    var unique = [];

    (codes || []).forEach(function (code) {
      code = String(code || '').toUpperCase().trim();

      if (code && unique.indexOf(code) === -1) {
        unique.push(code);
      }
    });

    var results = unique.map(function (code) {
      return resolve(code, v);
    });

    var insights = [];


    /*
      Multiple circuit faults in unrelated systems.

      This is intentionally broad. It does not claim which shared
      circuit is at fault; it simply tells the customer not to begin
      replacing several unrelated sensors independently.
    */

    var circuitResults = results.filter(function (r) {
      return r.circuitFault;
    });

    var circuitSystems = [];

    circuitResults.forEach(function (r) {
      if (
        r.system &&
        circuitSystems.indexOf(r.system) === -1
      ) {
        circuitSystems.push(r.system);
      }
    });

    if (
      circuitResults.length >= 2 &&
      circuitSystems.length >= 2
    ) {
      addInsight(
        insights,
        'multiple-circuit-faults',
        'Multiple electrical sensor faults detected',
        'Several different sensor or actuator circuits are faulting at the same time. Before replacing multiple components, inspect common power, ground, reference-voltage and harness connections and confirm all required sensors are connected.',
        {
          priority: 100,
          likelyVehicleSide: true
        }
      );
    }


    /*
      Explicit reference-voltage fault + other sensor circuits.
    */

    var hasReference = results.some(function (r) {
      return r.referenceVoltageFault;
    });

    if (hasReference && circuitResults.length >= 2) {
      addInsight(
        insights,
        'reference-voltage-correlation',
        'Reference-voltage fault may explain multiple sensor codes',
        'A sensor-reference-voltage fault is present along with other circuit faults. Diagnose the shared reference-voltage circuit before treating each sensor code as an independent failure.',
        {
          priority: 120,
          likelyVehicleSide: true
        }
      );
    }


    /*
      MAF/airflow + underboost/boost performance.
    */

    var hasAirflow = results.some(function (r) {
      return r.airflowFault;
    });

    var hasBoost = results.some(function (r) {
      return r.boostFault;
    });

    if (hasAirflow && hasBoost) {
      addInsight(
        insights,
        'airflow-boost-correlation',
        'Airflow and boost faults are present together',
        'Check the intake and charge-air system for leaks, restrictions or incorrect intake/MAF configuration before treating the faults independently. A datalog is recommended if the basic inspection passes.',
        {
          priority: 90,
          checkLeaks: true,
          logRecommended: true
        }
      );
    }


    /*
      Rail-pressure performance + rail-pressure sensor performance.
    */

    var hasRailPressure = results.some(function (r) {
      return r.railPressureFault;
    });

    var hasRailSensor = results.some(function (r) {
      return r.railPressureSensorFault;
    });

    if (hasRailPressure && hasRailSensor) {
      addInsight(
        insights,
        'rail-pressure-sensor-correlation',
        'Fuel-pressure and pressure-sensor faults are present together',
        'The ECM is reporting both a fuel-pressure performance problem and a rail-pressure-sensor problem. Verify that the pressure signal is credible before assuming the high-pressure fuel system itself is failing.',
        {
          priority: 95,
          logRecommended: true
        }
      );
    }


    /*
      Multiple fuel pressure faults.
    */

    var fuelPressureResults = results.filter(function (r) {
      return (
        r.railPressureFault ||
        r.railPressureSensorFault ||
        r.system === 'fuel_pressure'
      );
    });

    if (fuelPressureResults.length >= 2) {
      addInsight(
        insights,
        'multiple-fuel-pressure-codes',
        'Multiple fuel-pressure faults detected',
        'Treat these codes as one fuel-pressure-control problem rather than several unrelated faults. SFG will normally want commanded versus actual rail pressure and relevant regulator/supply data.',
        {
          priority: 80,
          logRecommended: true
        }
      );
    }


    /*
      VGT feedback + boost performance.
    */

    var hasVgtFeedback = results.some(function (r) {
      return r.vgtFeedbackFault;
    });

    if (hasVgtFeedback && hasBoost) {
      addInsight(
        insights,
        'vgt-feedback-boost-correlation',
        'Turbo-position feedback and boost performance faults are present together',
        'Verify turbo actuator/position feedback and wiring. If vane-position feedback exists on this application, SFG will want commanded versus actual vane position together with boost data.',
        {
          priority: 95,
          logRecommended: true
        }
      );
    }


    /*
      P0700 with no actual transmission-controller fault supplied.
    */

    var hasP0700 = unique.indexOf('P0700') >= 0;

    if (hasP0700) {
      addInsight(
        insights,
        'p0700-needs-tcm-codes',
        'Transmission-controller codes are still needed',
        'P0700 by itself does not identify the transmission problem. Scan the transmission controller and record the actual TCM fault codes.',
        {
          priority: 110,
          additionalCodesRequired: true
        }
      );
    }


    /*
      Network cluster.
    */

    var networkCount = results.filter(function (r) {
      return r.networkFault;
    }).length;

    if (networkCount >= 2) {
      addInsight(
        insights,
        'multiple-network-codes',
        'Multiple network communication faults detected',
        'Multiple modules are reporting communication faults. Check vehicle power and CAN/network integrity and remove unnecessary aftermarket OBD/CAN devices before assuming several modules failed independently.',
        {
          priority: 110,
          likelyVehicleSide: true
        }
      );
    }


    /*
      Aftertreatment / exhaust correlation.
    */

    var aftertreatmentCount = results.filter(function (r) {
      return r.aftertreatmentFault;
    }).length;

    if (aftertreatmentCount >= 2) {
      addInsight(
        insights,
        'multiple-aftertreatment-codes',
        'Multiple exhaust/aftertreatment faults detected',
        'Several exhaust or aftertreatment faults are present. Describe any exhaust or emissions-system changes made since the tune was ordered. Changes in exhaust hardware may require updated tuning.',
        {
          priority: 70,
          checkHardwareChanges: true,
          exhaustRelevant: true
        }
      );
    }


    /*
      Sort strongest diagnostic conclusions first.
    */

    insights.sort(function (a, b) {
      return (b.priority || 0) - (a.priority || 0);
    });


    return {
      vehicle: v,
      codes: unique,
      results: results,
      insights: insights,
      count: unique.length
    };
  }



  /* ==========================================================
     GENERIC SAE / ISO REFERENCE FALLBACK
     ========================================================== */

  /*
    SFG diagnostic rules above remain authoritative for automated
    troubleshooting. This reference layer is intentionally display-only:
      - it only runs for codes without an SFG exact/vehicle-specific rule;
      - it never changes title/customerText/category/action/diagnostic flags;
      - it never adds evidence or troubleshooting checks;
      - manufacturer-specific families are never looked up.

    OBDex publishes the generic SAE J2012 / ISO 15031-6 code layer as
    static CC0 JSON. If the public reference cannot be loaded, SFG's
    existing conservative unknown/family handling remains unchanged.
  */

  var GENERIC_REFERENCE_URL =
    'https://foerbsnavi.github.io/OBDex/generic.min.json';

  var GENERIC_REFERENCE_INDEX = null;
  var GENERIC_REFERENCE_PROMISE = null;

  function genericReferenceEligible(code) {
    code = String(code || '').toUpperCase().trim();

    return (
      /^P[023][0-9A-F]{3}$/.test(code) ||
      /^B0[0-9A-F]{3}$/.test(code) ||
      /^C0[0-9A-F]{3}$/.test(code) ||
      /^U[03][0-9A-F]{3}$/.test(code)
    );
  }

  function localizedReferenceText(value) {
    if (!value) return '';

    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'object') {
      return String(
        value.en ||
        value.EN ||
        value.english ||
        value.English ||
        ''
      ).trim();
    }

    return '';
  }

  function buildGenericReferenceIndex(rows) {
    var index = Object.create(null);

    (rows || []).forEach(function (row) {
      if (!row || !row.code) return;

      var code = String(row.code || '').toUpperCase().trim();
      if (!genericReferenceEligible(code)) return;

      index[code] = row;
    });

    return index;
  }

  function fetchGenericReferenceJson(url, timeoutMs) {
    if (typeof fetch !== 'function') {
      return Promise.resolve(null);
    }

    var controller =
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : null;

    var timer = null;

    if (controller && timeoutMs > 0) {
      timer = setTimeout(function () {
        try {
          controller.abort();
        } catch (error) {
          /* No action required. */
        }
      }, timeoutMs);
    }

    var options = {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      cache: 'force-cache'
    };

    if (controller) {
      options.signal = controller.signal;
    }

    return fetch(url, options).then(
      function (response) {
        if (timer) clearTimeout(timer);

        if (!response || !response.ok) {
          throw new Error(
            'Generic DTC reference request failed with HTTP ' +
            (response && response.status ? response.status : 'unknown')
          );
        }

        return response.json();
      },
      function (error) {
        if (timer) clearTimeout(timer);
        throw error;
      }
    );
  }

  function loadGenericReference(options) {
    options = options || {};

    if (GENERIC_REFERENCE_INDEX) {
      return Promise.resolve(GENERIC_REFERENCE_INDEX);
    }

    if (GENERIC_REFERENCE_PROMISE) {
      return GENERIC_REFERENCE_PROMISE;
    }

    var timeoutMs = parseInt(options.timeoutMs || 8000, 10);
    if (!isFinite(timeoutMs) || timeoutMs < 0) timeoutMs = 8000;

    GENERIC_REFERENCE_PROMISE = fetchGenericReferenceJson(
      options.url || GENERIC_REFERENCE_URL,
      timeoutMs
    ).then(function (rows) {
      if (!Array.isArray(rows)) {
        GENERIC_REFERENCE_PROMISE = null;
        return null;
      }

      GENERIC_REFERENCE_INDEX = buildGenericReferenceIndex(rows);
      return GENERIC_REFERENCE_INDEX;
    }).catch(function () {
      /*
        Fail closed: do not guess a definition and allow a later attempt
        to retry the public reference.
      */
      GENERIC_REFERENCE_PROMISE = null;
      return null;
    });

    return GENERIC_REFERENCE_PROMISE;
  }

  function applyGenericReference(result, entry) {
    if (!result || !entry) return result;

    var code = String(result.code || '').toUpperCase().trim();

    if (
      result.matchType !== 'family' &&
      result.matchType !== 'none'
    ) {
      return result;
    }

    if (!genericReferenceEligible(code)) {
      return result;
    }

    if (String(entry.code || '').toUpperCase().trim() !== code) {
      return result;
    }

    var title = localizedReferenceText(entry.title);
    var description = localizedReferenceText(entry.description);

    if (!title && !description) {
      return result;
    }

    var out = merge(result, {});

    out.genericReference = true;
    out.genericReferenceOnly = true;
    out.genericReferenceTitle = title;
    out.genericReferenceDescription = description;
    out.genericReferenceSource = 'OBDex generic SAE/ISO reference';

    return out;
  }

  function analysisNeedsGenericReference(analysis) {
    return !!(
      analysis &&
      Array.isArray(analysis.results) &&
      analysis.results.some(function (result) {
        if (!result) return false;

        return (
          (result.matchType === 'family' || result.matchType === 'none') &&
          genericReferenceEligible(result.code)
        );
      })
    );
  }

  function enrichAnalysisWithGenericReference(analysis, options) {
    if (!analysisNeedsGenericReference(analysis)) {
      return Promise.resolve(analysis);
    }

    return loadGenericReference(options).then(function (index) {
      var out = merge(analysis || {}, {});
      out.results = (analysis.results || []).map(function (result) {
        var code = String(result && result.code || '').toUpperCase().trim();
        if (!index || !index[code]) return result;
        return applyGenericReference(result, index[code]);
      });

      out.genericReferenceLookupAttempted = true;
      out.genericReferenceLookupAvailable = !!index;
      out.genericReferenceMatchCount = out.results.filter(function (result) {
        return !!(result && result.genericReference);
      }).length;

      return out;
    });
  }

  /* ==========================================================
     PUBLIC DATABASE
     ========================================================== */

  window.SFGEngineDTCData = {
    version: '2.1.0',

    /*
      Kept for backward compatibility with the current
      sfg-support-codes.js lookup logic.
    */
    exact: G,
    families: F,

    /*
      New vehicle-aware API.
    */
    vehicleRules: VEHICLE_RULES,
    normalizeVehicle: normalizeVehicle,
    resolve: resolve,
    analyzeSet: analyzeSet,

    /*
      Optional display-only generic-code reference. These helpers never
      replace SFG diagnostic rules or create diagnostic evidence.
    */
    genericReferenceUrl: GENERIC_REFERENCE_URL,
    genericReferenceEligible: genericReferenceEligible,
    loadGenericReference: loadGenericReference,
    applyGenericReference: applyGenericReference,
    enrichAnalysisWithGenericReference: enrichAnalysisWithGenericReference,

    profiles: {
      isGm66Diesel: isGm66Diesel,
      isModernGm66: isModernGm66,
      isPreLmlDuramax: isPreLmlDuramax,
      isLmlFamily: isLmlFamily,
      isLwn: isLwn,
      isFord67: isFord67,
      isRam67: isRam67,
      isRam59: isRam59
    }
  };

})();